import { useContext } from "react";
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import PageHero from "../components/PageHero";
import { LanguageContext } from "../App";
import { IMAGES, BOOKING_URL } from "../translations";

const BOOKING_LABELS = {
  it: {
    overline: "Prenotazione diretta",
    headline: "Prenota il tuo soggiorno",
    subtext: "Prenota direttamente dal nostro sito per il miglior prezzo garantito.",
    guarantee: "Miglior prezzo · Nessun intermediario · Conferma immediata",
    fallback: "Problemi con il modulo?",
    fallback_cta: "Prenota sul sito esterno",
  },
  en: {
    overline: "Direct booking",
    headline: "Book your stay",
    subtext: "Book directly on our website for the best price guaranteed.",
    guarantee: "Best price · No intermediary · Instant confirmation",
    fallback: "Issues with the form?",
    fallback_cta: "Book on external site",
  },
  de: {
    overline: "Direktbuchung",
    headline: "Ihren Aufenthalt buchen",
    subtext: "Buchen Sie direkt auf unserer Website für den garantiert besten Preis.",
    guarantee: "Bester Preis · Kein Vermittler · Sofortige Bestätigung",
    fallback: "Probleme mit dem Formular?",
    fallback_cta: "Extern buchen",
  },
  nl: {
    overline: "Directe reservering",
    headline: "Plan je verblijf",
    subtext: "Direct boeken op onze website voor de beste prijs gegarandeerd.",
    guarantee: "Beste prijs · Geen tussenpersoon · Directe bevestiging",
    fallback: "Problemen met het formulier?",
    fallback_cta: "Reserveer op de externe website",
  },
};

export default function BookingPage() {
  const { lang } = useContext(LanguageContext);
  const lb = BOOKING_LABELS[lang] || BOOKING_LABELS.en;
  const bookingUrl = BOOKING_URL[lang];

  return (
    <>
      <PageHero
        image={IMAGES.hero}
        overline={lb.overline}
        title={lb.headline}
        subtitle={lb.subtext}
      />

      {/* Guarantee strip */}
      <div className="bg-[#7B907F] py-4 px-6 text-center" data-testid="booking-guarantee">
        <p className="font-['Manrope',sans-serif] text-xs uppercase tracking-[0.25em] text-[#FDFBF7]/90">
          {lb.guarantee}
        </p>
      </div>

      {/* Booking iframe */}
      <section
        data-testid="booking-section"
        className="bg-[#FDFBF7] py-12 px-4 md:px-8"
      >
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full bg-white shadow-sm border border-[#E2DFD8] overflow-hidden"
            data-testid="booking-iframe-wrapper"
          >
            <iframe
              src={bookingUrl}
              title="SimpleBooking — Hotel Aurora Duino"
              width="100%"
              style={{ height: "900px", border: "none", display: "block" }}
              loading="eager"
              allow="payment"
              data-testid="booking-iframe"
            />
          </motion.div>

          {/* Fallback */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.5 }}
            className="mt-6 text-center"
          >
            <p className="font-['Manrope',sans-serif] text-xs text-[#9A9996] mb-3">
              {lb.fallback}
            </p>
            <a
              data-testid="booking-fallback-link"
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-['Manrope',sans-serif] text-xs uppercase tracking-[0.2em] text-[#1A1A19] border-b border-[#1A1A19] pb-0.5 hover:opacity-60 transition-opacity"
            >
              {lb.fallback_cta} <ExternalLink size={12} />
            </a>
          </motion.div>
        </div>
      </section>
    </>
  );
}
